"use server";

import { headers } from "next/headers";
import { redirect } from "next/navigation";
import { auth } from "@/auth";
import { db } from "@/lib/db";
import { validateCheckoutConsent } from "@/lib/consent";
import { createCheckout } from "@/lib/payments";
import { checkRateLimit } from "@/lib/rate-limit";

export async function checkoutCustomTour(
  tourId: string,
  _prevState: string | undefined,
  formData: FormData,
) {
  const tour = await db.tour.findUnique({ where: { id: tourId } });
  if (!tour || !tour.published || tour.kind !== "custom") {
    return "Ta oferta nie jest obecnie dostępna.";
  }

  const fullName = String(formData.get("fullName") ?? "").trim();
  const email = String(formData.get("email") ?? "").trim();
  const phone = String(formData.get("phone") ?? "").trim();
  const travelers = Number.parseInt(String(formData.get("travelers") ?? ""), 10);
  const destination = String(formData.get("destination") ?? "").trim();
  const description = String(formData.get("description") ?? "").trim();

  if (
    !fullName ||
    !email ||
    !destination ||
    !description ||
    Number.isNaN(travelers) ||
    travelers < 1
  ) {
    return "Uzupełnij wymagane pola poprawnie.";
  }

  const session = await auth();
  if (!session?.user) {
    redirect(`/login?callbackUrl=${encodeURIComponent("/tours/custom")}`);
  }

  const consentError = validateCheckoutConsent(formData);
  if (consentError) return consentError;

  if (!checkRateLimit(`checkout:${session.user.id}`, 10, 10 * 60 * 1000)) {
    return "Zbyt wiele prób zakupu. Spróbuj ponownie za chwilę.";
  }

  const order = await db.order.create({
    data: {
      userId: session.user.id,
      status: "pending",
      total: tour.price,
      currency: "PLN",
      consentAcceptedAt: new Date(),
      items: {
        create: {
          itemType: "tour",
          itemId: tour.id,
          titleSnapshot: tour.title,
          priceSnapshot: tour.price,
          meta: { fullName, email, phone: phone || undefined, travelers, destination, description },
        },
      },
    },
  });

  const headersList = await headers();
  const host = headersList.get("host");
  const protocol = host?.startsWith("localhost") || host?.startsWith("127.0.0.1") ? "http" : "https";
  const baseUrl = `${protocol}://${host}`;

  let checkoutSession;
  try {
    checkoutSession = await createCheckout({
      orderId: order.id,
      currency: "PLN",
      items: [{ title: tour.title, quantity: 1, unitAmount: tour.price }],
      customerEmail: session.user.email ?? undefined,
      successUrl: `${baseUrl}/account?checkout=success`,
      cancelUrl: `${baseUrl}/tours/custom?checkout=canceled`,
    });
  } catch (error) {
    console.error("checkoutCustomTour: createCheckout failed", error);
    return "Nie udało się rozpocząć płatności. Spróbuj ponownie.";
  }

  await db.order.update({
    where: { id: order.id },
    data: { provider: "stripe", providerRef: checkoutSession.providerRef },
  });

  redirect(checkoutSession.url);
}
